// Contrail V3.4.0 — export zones as Shapefile (.zip: .shp .shx .dbf .prj .cpg), GeoJSON or KML.
// Circles are exported as 64-point polygons (with their centre/radius kept in the attributes).
// Coordinates are WGS84 longitude/latitude (EPSG:4326).
import type { Zone } from "./org";
import { zoneFeature } from "./geo";

export type ExportZone = Zone & { company: string };
export type ExportFormat = "shp" | "geojson" | "kml";

const ring = (z: Zone) => (zoneFeature(z).geometry.coordinates[0] as [number, number][]).map(([x, y]) => [+x.toFixed(7), +y.toFixed(7)] as [number, number]);
const scope = (z: Zone) => (z.is_global ? "global" : "company");

export function toGeoJSON(zones: ExportZone[]) {
  return JSON.stringify({
    type: "FeatureCollection",
    name: "Contrail zones",
    crs: { type: "name", properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" } },
    features: zones.map((z) => ({
      type: "Feature",
      properties: { id: z.id, name: z.name, scope: scope(z), company: z.company, shape: z.kind, radius_m: z.kind === "circle" ? z.radius_m : null,
        center_lat: z.center_lat, center_lng: z.center_lng, color: z.color },
      geometry: { type: "Polygon", coordinates: [ring(z)] },
    })),
  }, null, 1);
}

const xml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" }[c]!));
/** KML colours are aabbggrr */
const kmlColour = (hex: string, alpha: string) => { const h = hex.replace("#", ""); return `${alpha}${h.slice(4, 6)}${h.slice(2, 4)}${h.slice(0, 2)}`.toLowerCase(); };

export function toKML(zones: ExportZone[], title = "Contrail zones") {
  const pm = zones.map((z) => `
    <Placemark>
      <name>${xml(z.name)}</name>
      <description>${xml(`${scope(z) === "global" ? "Global zone" : `Company zone · ${z.company}`} · ${z.kind}${z.kind === "circle" ? ` · radius ${z.radius_m} m` : ""}`)}</description>
      <Style><LineStyle><color>${kmlColour(z.color, "ff")}</color><width>2</width></LineStyle><PolyStyle><color>${kmlColour(z.color, "40")}</color></PolyStyle></Style>
      <ExtendedData><Data name="scope"><value>${scope(z)}</value></Data><Data name="company"><value>${xml(z.company)}</value></Data><Data name="id"><value>${z.id}</value></Data></ExtendedData>
      <Polygon><outerBoundaryIs><LinearRing><coordinates>${ring(z).map(([x, y]) => `${x},${y},0`).join(" ")}</coordinates></LinearRing></outerBoundaryIs></Polygon>
    </Placemark>`).join("");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${xml(title)}</name>${pm}\n</Document></kml>\n`;
}

// ---------------------------------------------------------------- Shapefile (polygon, type 5)
const PRJ = 'GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]]';

/** Shapefile outer rings are clockwise. */
function clockwise(r: [number, number][]) {
  let a = 0;
  for (let i = 0; i < r.length - 1; i++) a += (r[i + 1][0] - r[i][0]) * (r[i + 1][1] + r[i][1]);
  return a > 0 ? r : [...r].reverse();
}

export function toShapefile(zones: ExportZone[]) {
  const rings = zones.map((z) => { const r = clockwise(ring(z)); const c = r[0], l = r[r.length - 1]; return c[0] === l[0] && c[1] === l[1] ? r : [...r, c]; });
  const bbox = (r: [number, number][]) => [Math.min(...r.map((p) => p[0])), Math.min(...r.map((p) => p[1])), Math.max(...r.map((p) => p[0])), Math.max(...r.map((p) => p[1]))];
  const all = rings.flat();
  const box = all.length ? bbox(all) : [0, 0, 0, 0];
  const recLen = rings.map((r) => 4 + 32 + 4 + 4 + 4 + r.length * 16);        // bytes of record content
  const shpLen = 100 + recLen.reduce((s, n) => s + 8 + n, 0);
  const shp = new DataView(new ArrayBuffer(shpLen));
  const shx = new DataView(new ArrayBuffer(100 + rings.length * 8));
  const header = (v: DataView, bytes: number) => {
    v.setInt32(0, 9994, false); v.setInt32(24, bytes / 2, false); v.setInt32(28, 1000, true); v.setInt32(32, 5, true);
    box.forEach((n, i) => v.setFloat64(36 + i * 8, n, true));
  };
  header(shp, shpLen); header(shx, 100 + rings.length * 8);
  let o = 100;
  rings.forEach((r, i) => {
    shx.setInt32(100 + i * 8, o / 2, false); shx.setInt32(104 + i * 8, recLen[i] / 2, false);
    shp.setInt32(o, i + 1, false); shp.setInt32(o + 4, recLen[i] / 2, false); o += 8;
    shp.setInt32(o, 5, true); o += 4;
    bbox(r).forEach((n) => { shp.setFloat64(o, n, true); o += 8; });
    shp.setInt32(o, 1, true); shp.setInt32(o + 4, r.length, true); shp.setInt32(o + 8, 0, true); o += 12;
    for (const [x, y] of r) { shp.setFloat64(o, x, true); shp.setFloat64(o + 8, y, true); o += 16; }
  });

  // DBF (dBASE III) — UTF-8 text, declared in the .cpg
  const fields: [string, "C" | "N", number, number, (z: ExportZone) => string][] = [
    ["ID", "C", 36, 0, (z) => z.id], ["NAME", "C", 80, 0, (z) => z.name], ["SCOPE", "C", 8, 0, scope],
    ["COMPANY", "C", 100, 0, (z) => z.company], ["SHAPE", "C", 8, 0, (z) => z.kind],
    ["RADIUS_M", "N", 10, 0, (z) => (z.kind === "circle" && z.radius_m != null ? String(z.radius_m) : "")],
    ["CENTER_LAT", "N", 14, 7, (z) => z.center_lat?.toFixed(7) ?? ""], ["CENTER_LNG", "N", 14, 7, (z) => z.center_lng?.toFixed(7) ?? ""],
    ["COLOR", "C", 7, 0, (z) => z.color],
  ];
  const enc = new TextEncoder();
  const recSize = 1 + fields.reduce((s, f) => s + f[2], 0);
  const hdrSize = 32 + fields.length * 32 + 1;
  const dbf = new Uint8Array(hdrSize + zones.length * recSize + 1);
  const dv = new DataView(dbf.buffer);
  const now = new Date();
  dbf[0] = 3; dbf[1] = now.getFullYear() - 1900; dbf[2] = now.getMonth() + 1; dbf[3] = now.getDate();
  dv.setUint32(4, zones.length, true); dv.setUint16(8, hdrSize, true); dv.setUint16(10, recSize, true);
  fields.forEach(([name, type, len, dec], i) => {
    const b = 32 + i * 32;
    dbf.set(enc.encode(name).slice(0, 10), b); dbf[b + 11] = type.charCodeAt(0); dbf[b + 16] = len; dbf[b + 17] = dec;
  });
  dbf[hdrSize - 1] = 0x0d;
  zones.forEach((z, r) => {
    let p = hdrSize + r * recSize;
    dbf[p++] = 0x20;
    for (const [, type, len, , get] of fields) {
      let bytes = enc.encode(get(z));
      // trim on a character boundary
      while (bytes.length > len) bytes = enc.encode(new TextDecoder().decode(bytes.slice(0, bytes.length - 1)).replace(/�$/, ""));
      const cell = new Uint8Array(len).fill(0x20);
      if (type === "N") cell.set(bytes, len - bytes.length); else cell.set(bytes, 0);
      dbf.set(cell, p); p += len;
    }
  });
  dbf[dbf.length - 1] = 0x1a;
  return { shp: new Uint8Array(shp.buffer), shx: new Uint8Array(shx.buffer), dbf, prj: enc.encode(PRJ), cpg: enc.encode("UTF-8") };
}

// ---------------------------------------------------------------- tiny ZIP writer (stored, no compression)
const CRC = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
const crc32 = (d: Uint8Array) => { let c = 0xffffffff; for (let i = 0; i < d.length; i++) c = CRC[(c ^ d[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };

export function zip(files: { name: string; data: Uint8Array }[]) {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const d = new Date();
  const dosTime = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const dosDate = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  for (const f of files) {
    const name = enc.encode(f.name), crc = crc32(f.data), size = f.data.length;
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0x0800, true); lh.setUint16(8, 0, true);
    lh.setUint16(10, dosTime, true); lh.setUint16(12, dosDate, true); lh.setUint32(14, crc, true);
    lh.setUint32(18, size, true); lh.setUint32(22, size, true); lh.setUint16(26, name.length, true);
    parts.push(new Uint8Array(lh.buffer), name, f.data);
    const ch = new DataView(new ArrayBuffer(46));
    ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true); ch.setUint16(8, 0x0800, true);
    ch.setUint16(12, dosTime, true); ch.setUint16(14, dosDate, true); ch.setUint32(16, crc, true);
    ch.setUint32(20, size, true); ch.setUint32(24, size, true); ch.setUint16(28, name.length, true); ch.setUint32(42, offset, true);
    central.push(new Uint8Array(ch.buffer), name);
    offset += 30 + name.length + size;
  }
  const cSize = central.reduce((s, c) => s + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, cSize, true); end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, new Uint8Array(end.buffer)] as BlobPart[], { type: "application/zip" });
}

const slug = (s: string) => s.normalize("NFKD").replace(/[^\w\s-]/g, "").trim().replace(/[\s_]+/g, "-").replace(/-+/g, "-") || "zones";

/** Build the file for download. `label` names the set, e.g. "Global", "Bula Transport", "Nadi Airport". */
export function exportZones(zones: ExportZone[], format: ExportFormat, label: string): { blob: Blob; filename: string } {
  const base = `Contrail_Zones_${slug(label)}_${new Date().toISOString().slice(0, 10)}`;
  if (format === "geojson") return { blob: new Blob([toGeoJSON(zones)], { type: "application/geo+json" }), filename: `${base}.geojson` };
  if (format === "kml") return { blob: new Blob([toKML(zones, `Contrail zones · ${label}`)], { type: "application/vnd.google-earth.kml+xml" }), filename: `${base}.kml` };
  const s = toShapefile(zones);
  return { blob: zip([
    { name: `${base}.shp`, data: s.shp }, { name: `${base}.shx`, data: s.shx }, { name: `${base}.dbf`, data: s.dbf },
    { name: `${base}.prj`, data: s.prj }, { name: `${base}.cpg`, data: s.cpg },
  ]), filename: `${base}.zip` };
}

export function download(blob: Blob, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
